import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { ResolvedUser } from 'src/app/@data-services/user-auth/user-auth.data-model';
import { UserAuthService } from 'src/app/@services/user-auth.service';
import { AuthStateService } from 'src/app/@store/auth-state.service';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';
import { AUTH_ROUTES } from '../constants/auth-constants';
import { AuthSessionService } from '../services/auth-session';

const INTERNAL_EMAIL_DOMAIN = 'rozie.ai';

export const attendeeAuthGuard: CanActivateFn = (
  _route,
  state
): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const sessionService = inject(AuthSessionService);
  const userAuthService = inject(UserAuthService);
  const authStateService = inject(AuthStateService);
  const eventConfigStateService = inject(EventConfigStateService);

  const eventName = eventConfigStateService.$eventIdentifier();
  const returnUrl = state.url.startsWith('/') ? state.url : AUTH_ROUTES.HOME;

  if (!userAuthService.isAuthenticationRequired()) {
    return of(true);
  }

  if (eventName && userAuthService.checkExistingAuth(eventName)) {
    const status = authStateService.$status();
    if (
      status === 'blocked' ||
      status === 'no_entitlement' ||
      status === 'error'
    ) {
      return of(
        router.createUrlTree([AUTH_ROUTES.LOGIN], {
          queryParams: { returnUrl },
        })
      );
    }
    return of(true);
  }

  return sessionService.checkSession$().pipe(
    take(1),
    switchMap((session) => {
      if (!session.isAuthenticated) {
        return of(
          router.createUrlTree([AUTH_ROUTES.LOGIN], {
            queryParams: { returnUrl },
          })
        );
      }

      return sessionService.getUserEmail$().pipe(
        take(1),
        switchMap((email) => {
          if (!email) {
            return of(
              router.createUrlTree([AUTH_ROUTES.LOGIN], {
                queryParams: { returnUrl },
              })
            );
          }

          const emailDomain = email.split('@')[1]?.toLowerCase();
          if (emailDomain === INTERNAL_EMAIL_DOMAIN && eventName) {
            const internalUser: ResolvedUser = {
              userId: email,
              registrationId: 'internal',
              email,
              entitlements: [],
            };
            authStateService.setEventNameIdentifier(eventName);
            authStateService.setUser(internalUser);
            return of(true);
          }

          if (!eventName) {
            return of(true);
          }

          return userAuthService.authenticateWithEmail(eventName, email).pipe(
            take(1),
            map((result) => {
              if (result.success) {
                return true;
              }

              const authStatus = authStateService.$status();
              if (
                authStatus === 'blocked' ||
                authStatus === 'no_entitlement' ||
                authStatus === 'unresolved' ||
                authStatus === 'error'
              ) {
                return router.createUrlTree([AUTH_ROUTES.LOGIN], {
                  queryParams: { returnUrl },
                });
              }

              return router.createUrlTree([AUTH_ROUTES.LOGIN], {
                queryParams: { returnUrl },
              });
            }),
            catchError((error) => {
              if (
                error instanceof HttpErrorResponse &&
                (error.status === 401 ||
                  error.status === 403 ||
                  error.status === 404)
              ) {
                return of(
                  router.createUrlTree([AUTH_ROUTES.LOGIN], {
                    queryParams: { returnUrl },
                  })
                );
              }
              return of(true);
            })
          );
        })
      );
    }),
    catchError(() =>
      of(
        router.createUrlTree([AUTH_ROUTES.LOGIN], {
          queryParams: { returnUrl },
        })
      )
    )
  );
};
